import type { QueryClient } from '@tanstack/react-query'

import type { TaskWithAssignments } from '@scrambl/shared'
import { isOfflineRecordRevision } from '@scrambl/shared/offline'

const TASK_CACHE_ROOTS = new Set([
  'task',
  'tasks',
  'my-tasks',
  'subtasks',
  'project-subtask-index',
])

function* matchingTasks(
  data: unknown,
  taskId: string,
): Generator<TaskWithAssignments> {
  if (!data || typeof data !== 'object') return
  if (Array.isArray(data)) {
    for (const item of data) yield* matchingTasks(item, taskId)
    return
  }
  const record = data as Record<string, unknown>
  if (record.id === taskId) {
    yield record as TaskWithAssignments
    return
  }
  for (const key of ['tasks', 'data', 'pages']) {
    if (Array.isArray(record[key])) yield* matchingTasks(record[key], taskId)
  }
}

/** Counts/deltas use the newest known row, not whichever cache happens to be visited first. */
export function getCachedTaskSnapshot(
  client: QueryClient,
  taskId: string,
): TaskWithAssignments | undefined {
  let selected: TaskWithAssignments | undefined
  const queries = client.getQueriesData<unknown>({
    predicate: query => TASK_CACHE_ROOTS.has(String(query.queryKey[0])),
  })
  for (const [, data] of queries) {
    for (const task of matchingTasks(data, taskId)) {
      if (
        !selected ||
        (isOfflineRecordRevision(task.revision) &&
          (!isOfflineRecordRevision(selected.revision) ||
            task.revision > selected.revision))
      )
        selected = task
    }
  }
  return selected
}
